// @ts-nocheck
import throttle from 'lodash/throttle';
import assign from 'lodash/assign';
import get from 'lodash/get';
import debounce from 'lodash/debounce';

import ResizeSensor from 'css-element-queries/src/ResizeSensor';
import {VideoMetaData} from '../../utils/ts-video-subscriber/video-positioning/abstractions';
import {OldMinimizedVideoStrategy} from '../../utils/ts-video-subscriber/video-positioning/minimized-old-video';
import {StretchedVideoStrategy} from '../../utils/ts-video-subscriber/video-positioning/maximized-stretched-video';
import {MinimizedCircleVideoStrategy} from '../../utils/ts-video-subscriber/video-positioning/minimized-circle-video';
import {MinimizedSquareVideoStrategy} from '../../utils/ts-video-subscriber/video-positioning/minimized-square-video';
import {KnownMediaStream, VideoResolutionFormat} from '@techsee/techsee-media-service/lib/MediaConstants';
import {
    MousePressStatus,
    mouseClickType,
    MouseButtonPosition
} from '@techsee/techsee-common/lib/constants/remote-control.constants';
import {UserType} from '@techsee/techsee-common/lib/constants/room.constants';
import {getAppTracer} from '../../../app.tracer';
import {CanvasDrawer} from '@techsee/techsee-client-services/lib/services/CanvasDrawerService';
import {CanvasEvents} from '@techsee/techsee-client-infra/lib/helpers/CanvasEvents';
import {VIDEO_MINIMIZED_MODES} from '../../../states/dashboard/main/main.helper';
import {mediaFrameUIParams} from '../../constants/media-frame-ui-params/media-frame-ui-params.constant';
import {VIDEO_MODES as MODES} from '../../../states/dashboard/main/main.settings';
import {reaction, runInAction, comparer} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
const trace = getAppTracer('TsVideoSubscriberController');

type TimeoutHandle = ReturnType<typeof setTimeout>;

export enum VideoSubscriberEventType {
    on_touch_start = 'on_touch_start',
    on_mouse_down = 'on_mouse_down',
    on_click = 'on_click',
    on_pre_video_click = 'on_pre_video_click',
    on_init_canvas_click = 'on_init_canvas_click',
    on_video_clicked = 'on_video_clicked',
    on_pause_video = 'on_pause_video',
    on_handle_dashboard_title_action = 'on_handle_dashboard_title_action',
    on_toggle_mute = 'on_toggle_mute',
    on_magic_marker = 'on_magic_marker',
    on_restore_from_focus_loss = 'on_restore_from_focus_loss',
    on_video_subscriber_changed = 'on_video_subscriber_changed',
    on_video_resolution_changed = 'on_video_resolution_changed'
}

export interface TsVideoSubscriberControllerDeps {
    chatApi: any;
    environmentDetect: any;
    displayTabletAsDesktop: boolean;
    tsScanArea: any;
    tsScan?: {emit: (eventName: string, payload: any) => void};
    dashboardState: any;
    tsInterfaceHelper: any;
    tsSnapshotReminder: any;
    responsiveUtils: any;
    eventLog: any;
    mediaService: any;
    isMiniDashboard: boolean;
    isLivePointerEnabled: boolean;
    isMinimizedToBottomRight: boolean;
}

export class TsVideoSubscriberController extends TechseeBaseController {
    private readonly chatApi: any;
    private readonly environmentDetect: any;
    private readonly displayTabletAsDesktop: boolean;
    private readonly tsScanArea: any;
    private readonly tsScan?: {emit: (eventName: string, payload: any) => void};
    private readonly dashboardState: any;
    private readonly tsInterfaceHelper: any;
    private readonly tsSnapshotReminder: any;
    private readonly responsiveUtils: any;
    private readonly mediaService: any;
    private readonly eventLog: any;
    private readonly isMiniDashboard: boolean;
    private readonly isLivePointerEnabled: boolean;
    private readonly isMinimizedToBottomRight: boolean;

    private isIE11: boolean;
    private canvasDrawer: any;
    private _pointer: HTMLElement | null = null;
    private isTablet: boolean;
    private _eventLog: any;
    private _element: HTMLElement | null = null;
    private _containerWidth = 1;
    private _containerHeight = 1;
    private _isHeld = false;
    private _isMoving = false;
    /** Captured at pointer start; preVideoClick mutates focus before our handler runs (SI-1431) */
    private _hadVideoFocusAtPointerStart = false;
    private isPainting = false;
    private _destroyed: boolean = false;
    private mouseClicks: number | null = null;
    private mousePressStatus: MousePressStatus | null = null;
    private mouseButtonPosition = MouseButtonPosition.Left;
    private updateCursorPosition: (event: any, extraCursorData?: any) => void;
    private mouseClickType = mouseClickType.Click;
    private _disposers: Array<() => void> = [];
    private _frame = {left: 0, top: 0, width: 1, height: 1};
    private _annotationCanvas: HTMLCanvasElement | null = null;
    private _localDrawingCanvas: HTMLCanvasElement | null = null;
    private _remoteDrawingCanvas: HTMLCanvasElement | null = null;
    private _onDraw: ((...args: any[]) => void) | null = null;
    private _onClientDraw: ((...args: any[]) => void) | null = null;
    private _onDashboardDraw: ((...args: any[]) => void) | null = null;
    private mobile = false;
    private isPhone = false;
    private isTouchCapableDesktop = false;
    private minimizedVideoMoved = false;
    private isFullScreenVideo = true;
    private isMovingTimeout?: TimeoutHandle;
    private timer?: TimeoutHandle;
    private timeOutId?: TimeoutHandle;
    private _mainContainerSizeSensor: ResizeSensor | null = null;
    private mainContainerElement: HTMLElement | null = null;
    private elementListeners: Array<{target: EventTarget; type: string; handler: EventListener}> = [];
    private trackContainerListeners: Array<{target: EventTarget; type: string; handler: EventListener}> = [];
    private readonly scrollScreen!: ReturnType<typeof debounce>;
    private remoteCanvasDrawer: CanvasDrawer | null = null;
    private currentDrawingSource: UserType | null = null;
    private videoResolutionValue: any;
    private readonly window: window;
    private readonly updateFrame!: ReturnType<typeof throttle>;

    public videoState: any;
    public canTakeScreenshot: boolean = true;
    public videoSubscriber: any;

    constructor(deps: TsVideoSubscriberControllerDeps) {
        super();

        this.chatApi = deps.chatApi;
        this.environmentDetect = deps.environmentDetect;
        this.displayTabletAsDesktop = deps.displayTabletAsDesktop;
        this.tsScanArea = deps.tsScanArea;
        this.tsScan = deps.tsScan;
        this.dashboardState = deps.dashboardState;
        this.mediaFrameUIParams = mediaFrameUIParams;
        this.tsInterfaceHelper = deps.tsInterfaceHelper;
        this.tsSnapshotReminder = deps.tsSnapshotReminder;
        this.isMinimizedToBottomRight = deps.isMinimizedToBottomRight;
        this.isLivePointerEnabled = deps.isLivePointerEnabled;
        this.isMiniDashboard = deps.isMiniDashboard;
        this.mediaService = deps.mediaService;

        this.canvasDrawer = null;
        this.responsiveUtils = deps.responsiveUtils;
        this.window = window;
        this._pointer = null;
        this.isTablet = this.environmentDetect.isTablet(this.displayTabletAsDesktop);
        this.isIE11 = this.environmentDetect.isIE11();
        this._eventLog = deps.eventLog;
        this._element = null;
        this._containerWidth = 1;
        this._containerHeight = 1;
        this._isHeld = false;
        this._isMoving = false;
        this.isPainting = false;
        this.mouseClicks = null;
        this.mousePressStatus = null;
        this.mouseButtonPosition = MouseButtonPosition.Left;
        this.updateCursorPosition = debounce(this._updateCursorPosition.bind(this), 5);
        this.mouseClickType = mouseClickType.Click;
        this.videoState = this.dashboardState.videoModeState.videoState;
        this._frame = {left: 0, top: 0, width: 1, height: 1};

        this._disposers.push(
            reaction(
                () => !this.videoState.focus,
                () => {
                    this._updateFrameAndOffset();
                    this.videoResolutionValue && this._videoResolutionDimensionChange(this.videoResolutionValue);
                }
            )
        );

        this._disposers.push(
            reaction(
                () => this.videoState.mode,
                () => {
                    this._isHeld = false;
                    this._isMoving = false;

                    if (this.videoSubscriber) {
                        this.applySettings();
                    }
                }
            )
        );

        this._disposers.push(
            reaction(
                () => this.videoState.focus,
                (focus) => {
                    if (!focus) {
                        this._detectCursor(null);
                    }
                    this._updateAfterProcessHasEnded();
                }
            )
        );

        this._disposers.push(
            reaction(
                () => this.videoState.isClientVisible,
                () => {
                    this._updateFrameAndOffset();
                }
            )
        );

        this._disposers.push(
            reaction(
                () => this.dashboardState.minimizedVideoMode,
                (newMode, oldMode) => {
                    if (newMode !== oldMode && !this.videoState.focus && this.videoSubscriber) {
                        this.applySettings();
                        this._updateFrameAndOffset();
                    }
                },
                {fireImmediately: true}
            )
        );

        this._disposers.push(
            reaction(
                () => this.videoState.enabled,
                (enabled) => {
                    if (enabled && this._element) {
                        if (!this.videoSubscriber) {
                            this.init();
                        }
                        this.applySettings();
                    }
                },
                {fireImmediately: false}
            )
        );

        this._videoSubscriberStateChanged = this._videoSubscriberStateChanged.bind(this);
        this.updateFrame = throttle(this._updateFrame.bind(this), this.isIE11 ? 1000 : 200);
        this._updateAfterProcessHasEnded = this._updateAfterProcessHasEnded.bind(this);
        this._updateFrameAndOffset = this._updateFrameAndOffset.bind(this);

        this._scrollScreenOnce = this._scrollScreenOnce.bind(this);
        this.scrollScreen = debounce(this._scrollScreenOnce, 5, {
            leading: true,
            trailing: false
        });

        this._videoResolutionDimensionChange = this._videoResolutionDimensionChange.bind(this);
        this._handleClickType = this._handleClickType.bind(this);
    }
    private onMagicMarkerReceived = () => {
        this.emit(VideoSubscriberEventType.on_magic_marker, false);
    };

    private onVideoResolutionChanged = (data: {resolutionFormat: any}) => {
        this.emit(VideoSubscriberEventType.on_video_resolution_changed, data);
    };

    private onMagicMarkerDrawn = () => {
        this.emit(VideoSubscriberEventType.on_magic_marker, true);
    };

    private restoreFromFocusLoss = () => {
        this.emit(VideoSubscriberEventType.on_restore_from_focus_loss, null);
    };

    private onVideoSubscriberChanged = (subscriber: any) => {
        this.emit(VideoSubscriberEventType.on_video_subscriber_changed, subscriber);
    };

    private normalizeElement(element: any): HTMLElement | null {
        if (!element) {
            return null;
        }
        if (element instanceof HTMLElement) {
            return element;
        }
        if (typeof element === 'object' && 'nativeElement' in element) {
            return this.normalizeElement(element.nativeElement);
        }
        if (Array.isArray(element) || typeof element.length === 'number') {
            return this.normalizeElement(element[0]);
        }
        return null;
    }

    private getClientPosition(event: Event): {clientX: number; clientY: number} {
        if ('touches' in event && event.touches?.length) {
            return {clientX: event.touches[0].clientX, clientY: event.touches[0].clientY};
        }
        if ('changedTouches' in event && event.changedTouches?.length) {
            return {clientX: event.changedTouches[0].clientX, clientY: event.changedTouches[0].clientY};
        }
        const mouseEvent = event as MouseEvent;
        return {clientX: mouseEvent.clientX, clientY: mouseEvent.clientY};
    }

    private addListener(target: EventTarget | null, type: string, handler: EventListener) {
        if (!target) {
            return;
        }
        target.addEventListener(type, handler);
        this.elementListeners.push({target, type, handler});
    }

    private addTrackContainerListener(target: EventTarget | null, type: string, handler: EventListener) {
        if (!target) {
            return;
        }
        target.addEventListener(type, handler);
        this.trackContainerListeners.push({target, type, handler});
    }

    private removeRegisteredListeners(collection: Array<{target: EventTarget; type: string; handler: EventListener}>) {
        collection.forEach(({target, type, handler}) => target.removeEventListener(type, handler));
        collection.length = 0;
    }

    private setStyles(element: HTMLElement | null, styles: {[key: string]: string | number}) {
        if (!element) {
            return;
        }
        Object.keys(styles).forEach((key) => {
            const value = styles[key];
            (element.style as any)[key] = typeof value === 'number' ? `${value}px` : value;
        });
    }

    private appendChild(parent: HTMLElement | null, child: HTMLElement | null) {
        if (parent && child && !parent.contains(child)) {
            parent.appendChild(child);
        }
    }

    private hideElement(element: HTMLElement | null) {
        if (element) {
            element.style.display = 'none';
        }
    }

    private showElement(element: HTMLElement | null) {
        if (element) {
            element.style.display = '';
        }
    }

    private setDatasetValue(element: HTMLElement | null, key: string, value: string | undefined) {
        if (!element) {
            return;
        }
        if (value === undefined) {
            delete (element.dataset as any)[key];
        } else {
            (element.dataset as any)[key] = value;
        }
    }

    private getDatasetValue(element: HTMLElement | null, key: string) {
        if (!element) {
            return undefined;
        }
        return (element.dataset as any)[key];
    }

    private getBoundingClientRect(element: HTMLElement | null) {
        return element ? element.getBoundingClientRect() : {width: 0, height: 0, top: 0, left: 0};
    }

    private getElementWidth(element: HTMLElement | null): number {
        return element ? element.getBoundingClientRect().width : 0;
    }

    private getElementHeight(element: HTMLElement | null): number {
        return element ? element.getBoundingClientRect().height : 0;
    }

    private setDimensions(element: HTMLElement | null, width?: number, height?: number) {
        if (!element) {
            return;
        }
        if (typeof width === 'number') {
            element.style.width = `${width}px`;
        }
        if (typeof height === 'number') {
            element.style.height = `${height}px`;
        }
    }

    private toggleClass(element: HTMLElement | null, className: string, force?: boolean) {
        if (!element) {
            return;
        }
        element.classList.toggle(className, force);
    }

    private getOffset(element: HTMLElement | null) {
        if (!element) {
            return {top: 0, left: 0};
        }
        const rect = element.getBoundingClientRect();
        return {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX
        };
    }

    get videoStreamWidth() {
        return this.videoSubscriber && this.videoSubscriber.mediaElement ? this.videoSubscriber.videoWidth : 0;
    }

    get videoStreamHeight() {
        return this.videoSubscriber && this.videoSubscriber.mediaElement ? this.videoSubscriber.videoHeight : 0;
    }

    get annotationCanvas(): HTMLCanvasElement | null {
        if (!this._annotationCanvas && this._element) {
            this._annotationCanvas = this._element.querySelector('.video-annotations') as HTMLCanvasElement | null;
        }
        return this._annotationCanvas;
    }

    get localDrawingCanvas(): HTMLCanvasElement | null {
        if (!this._localDrawingCanvas && this._element) {
            this._localDrawingCanvas = this._element.querySelector('.local-drawing-canvas') as HTMLCanvasElement | null;
        }
        return this._localDrawingCanvas;
    }

    get remoteDrawingCanvas(): HTMLCanvasElement | null {
        if (!this._remoteDrawingCanvas && this._element) {
            this._remoteDrawingCanvas = this._element.querySelector(
                '.remote-drawing-canvas'
            ) as HTMLCanvasElement | null;
        }
        return this._remoteDrawingCanvas;
    }

    _isAnnotationMode() {
        const mode = this.videoState.mode,
            {SCANNING} = MODES;

        return mode === SCANNING;
    }

    setElement(element: HTMLElement | null) {
        this._element = this.normalizeElement(element);
    }

    init() {
        if (!this._element) {
            trace.error('TsVideoSubscriberController.init was called without a valid element');
            return;
        }

        this._onDraw = (param, data) => {
            this._updateCanvas(param, data);

            if (data.length) {
                if (this.onMagicMarkerReceived) {
                    this.onMagicMarkerReceived();
                }

                this._eventLog.magicMarkerDrawingReceived();
            }
        };

        this._onClientDraw = (param, data) => {
            this.currentDrawingSource = UserType.client;
            this._onDraw(param, data);
        };

        this._onDashboardDraw = (param, data) => {
            this.currentDrawingSource = UserType.dashboard;
            this._onDraw(param, data);
        };

        this.mobile = this.environmentDetect.isMobile();
        this.isPhone = this.environmentDetect.isPhone();
        this.isTouchCapableDesktop =
            this.environmentDetect.isTouchCapableDesktop?.(this.displayTabletAsDesktop) ?? false;

        if (!this._pointer && this.mobile && this.videoState.enableDashboardMagicMarker) {
            this._pointer = document.createElement('div');
            this._pointer.classList.add('live-pointer-mobile');
            this.hideElement(this._pointer);
            this.appendChild(this._element, this._pointer);
        }

        this.chatApi.on('clientDrawings', this._onClientDraw);
        this.chatApi.on('dashboardDrawings', this._onDashboardDraw);

        const isTouchEventFlow = this.environmentDetect.shouldUseTouchInputFlow(this.displayTabletAsDesktop);
        const [start, move, cancel, end] = isTouchEventFlow
            ? ['touchstart', 'touchmove', 'touchcancel', 'touchend']
            : ['mousedown', 'mousemove', 'mouseleave', 'mouseup'];
        let startX = 0;
        let startY = 0;

        const startHandler = (event: Event) => {
            const canvas = this.annotationCanvas;

            this.canTakeScreenshot = true;
            this._hadVideoFocusAtPointerStart = this.videoState.focus;

            if (this.dashboardState.videoModeState.videoState.remoteControl) {
                this._mouseClick(event as MouseEvent);
                const extraCursorData = {type: this.mouseClickType};

                this.mousePressStatus = MousePressStatus.Down;
                this.updateCursorPosition(event, extraCursorData);
            }

            if (event.target !== canvas) {
                this._isHeld = true;

                return;
            }

            this._isHeld = true;
            ({x: startX, y: startY} = this._getCursorPos(event));
        };

        const moveHandler = (event: Event) => {
            if (this._isHeld && !this.tsScanArea.initialized) {
                // Set _isMoving immediately to distinguish drag from click
                this._isMoving = true;

                if (!this.isMovingTimeout) {
                    this.isMovingTimeout = setTimeout(() => {
                        if (this.isIE11) {
                            this.applySettings();
                        }
                    }, 200);
                }

                const {clientX, clientY} = this.getClientPosition(event);
                const elementRect = this._element.getBoundingClientRect();
                const x = clientX - elementRect.width / 2;
                const y = clientY - elementRect.height / 2;

                if (!this.videoState.focus) {
                    this.setStyles(this._element, {
                        top: `${y}px`,
                        left: `${x}px`,
                        right: 'auto'
                    });
                }

                if (this._isAnnotationMode() && !this.dashboardState.isToggleEnabled_ScanCaptureDesign) {
                    const canvas = this.annotationCanvas;
                    if (canvas) {
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.clearRect(0, 0, canvas.width, canvas.height);

                            const {x: cx, y: cy} = this._getCursorPos(event);

                            const sx = cx > startX ? startX : cx;
                            const sy = cy > startY ? startY : cy;
                            const width = Math.abs(cx - startX);
                            const height = Math.abs(cy - startY);

                            ctx.strokeStyle = 'green';
                            ctx.strokeRect(sx, sy, width, height);
                        }
                    }
                }

                event.preventDefault();
            }
        };

        const cancelHandler = () => {
            this.canTakeScreenshot = false;

            if (!this._isAnnotationMode()) {
                this._isHeld = false;
            }

            if (this.isMovingTimeout) {
                clearTimeout(this.isMovingTimeout);
                this.isMovingTimeout = undefined;
            }

            this._isMoving = false;
        };

        const endHandler = (event: Event) => {
            if (this.dashboardState.videoModeState.videoState.remoteControl) {
                this._handleClickType();
                this._mouseClick(event as MouseEvent);
                const extraCursorData = {type: this.mouseClickType};

                this.mousePressStatus = MousePressStatus.Up;
                this.updateCursorPosition(event, extraCursorData);
            }

            if (this.isMovingTimeout) {
                clearTimeout(this.isMovingTimeout);
                this.isMovingTimeout = undefined;
            }

            if (this._isMoving && this._isAnnotationMode()) {
                const {x, y} = this._getCursorPos(event),
                    rect = {
                        x: x > startX ? startX : x,
                        y: y > startY ? startY : y,
                        width: Math.abs(x - startX),
                        height: Math.abs(y - startY)
                    };

                if (this.videoState.mode === MODES.SCANNING && !this.dashboardState.isToggleEnabled_ScanCaptureDesign) {
                    if (this.tsScan) {
                        this.tsScan.emit('areaSelected', {rect});
                    } else if (typeof this.tsScanArea.emit === 'function') {
                        this.tsScanArea.emit('areaSelected', {rect});
                    }
                }
            }

            if (!this._isMoving && !this.videoState.focus) {
                this.sendEvent(VideoSubscriberEventType.on_pre_video_click, event);
                this.restoreFromFocusLoss();
            }

            this._isHeld = false;
            setTimeout(() => (this._isMoving = false));
        };

        this.addListener(this._element, start, startHandler as EventListener);
        this.addListener(this._element, move, moveHandler as EventListener);
        this.addListener(this._element, cancel, cancelHandler as EventListener);
        this.addListener(this._element, end, endHandler as EventListener);

        if (this.isTouchCapableDesktop) {
            this.addListener(this._element, 'touchstart', startHandler as EventListener);
            this.addListener(this._element, 'touchmove', moveHandler as EventListener);
            this.addListener(this._element, 'touchcancel', cancelHandler as EventListener);
            this.addListener(this._element, 'touchend', endHandler as EventListener);
        }

        if (this.dashboardState.videoModeState.videoState.remoteControl) {
            const keyboardHandler = (event: KeyboardEvent) => {
                this.chatApi.setStatusFast('keyboard', {
                    altKey: event.altKey,
                    ctrlKey: event.ctrlKey,
                    shiftKey: event.shiftKey,
                    key: event.key,
                    keyCode: event.keyCode,
                    type: event.type
                });
            };
            document.addEventListener('keydown', keyboardHandler);
            document.addEventListener('keyup', keyboardHandler);

            // Store references for cleanup
            this.elementListeners.push({target: document, type: 'keydown', handler: keyboardHandler});
            this.elementListeners.push({target: document, type: 'keyup', handler: keyboardHandler});

            window.addEventListener('wheel', this.scrollScreen);
        }

        window.addEventListener('resize', this._updateAfterProcessHasEnded);

        this._initMainContainerSizeSensor();
    }

    onDrawingCanvasStartDraw(e) {
        if (!this.videoState.isPaused) {
            const {
                detail: {data}
            } = e;
            const {x, y} = this._getCursorPos(data);

            this.chatApi.setStatusFast('drawings', [], true);

            if (this.canvasDrawer) {
                this.canvasDrawer.startDrawing(x, y);
            }
        }
    }

    onDrawingCanvasDraw(e) {
        if (!this.videoState.isPaused && this.canvasDrawer && this.canvasDrawer.isPainting) {
            const {
                detail: {data}
            } = e;
            const {x, y} = this._getCursorPos(data);

            const localCanvas =
                this.localDrawingCanvas instanceof HTMLElement ? this.localDrawingCanvas : this.localDrawingCanvas?.[0];

            if (localCanvas) {
                this.canvasDrawer.addToDrawing(x, y, {
                    width: localCanvas.width,
                    height: localCanvas.height
                });
            }
        }

        if (e && this.videoState.focus && this.videoState.enabled && !this.videoState.isPaused) {
            const {
                detail: {data}
            } = e;

            this.updateCursorPosition(data);
        }
    }

    onDrawingCanvasEndDraw() {
        if (!this.canvasDrawer || !this.canvasDrawer.isPainting) {
            return;
        }

        const getDrawings = this.canvasDrawer.getDrawings();

        if (!this.videoState.isPaused) {
            this.chatApi.setStatusFast('drawings', getDrawings, true);
        }

        this.canvasDrawer.endDrawing();

        if (getDrawings.length && !this.videoState.isPaused) {
            if (this.onMagicMarkerDrawn) {
                this.onMagicMarkerDrawn();
            }

            this._eventLog.magicMarkerDrawingSent();
        }
    }

    initLocalDrawingCanvas(element) {
        if (this.canvasDrawer) {
            return;
        }

        const canvasEventsHelper = new CanvasEvents();
        const floatingElements = [];

        const drawingOptions = {
            lineWidth: this.videoState.magicMarker.dashboard.lineWidth,
            lineWidthRefResolution: {width: 640, height: 480},
            strokeColorRGB: this.videoState.magicMarker.dashboard.strokeColorRgb
        };
        const timeUntilDrawingStartFading = this.videoState.magicMarker.dashboard.timeUntilDrawingStartFading;

        this.canvasDrawer = new CanvasDrawer(element, drawingOptions, undefined, timeUntilDrawingStartFading);

        if (!this.isMiniDashboard && !this.mobile) {
            const floatingBar = document.querySelector('ts-floating-toolbar');
            const snapshotButton = document.querySelector('.magic-marker-screenshot');

            floatingElements.push(floatingBar);
            floatingElements.push(snapshotButton);
        }

        if (this.isMiniDashboard) {
            const miniDashboardImgStrip = document.querySelector('.ts-mini-dashboard-img-strip');

            floatingElements.push(miniDashboardImgStrip);
        }
        const touchRelevant = this.environmentDetect.shouldUseTouchInputFlow(this.displayTabletAsDesktop);

        canvasEventsHelper.initListeners(element, touchRelevant, floatingElements, this.isTouchCapableDesktop);

        element.addEventListener('drawingCanvasStartDraw', (e) => this.onDrawingCanvasStartDraw(e));
        element.addEventListener('drawingCanvasDraw', (e) => this.onDrawingCanvasDraw(e));
        element.addEventListener('drawingCanvasEndDraw', () => this.onDrawingCanvasEndDraw());
    }

    destroy() {
        if (this._destroyed) {
            return;
        }
        this._destroyed = true;

        if (this.timeOutId) {
            clearTimeout(this.timeOutId);
            this.timeOutId = undefined;
        }
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }
        if (this.isMovingTimeout) {
            clearTimeout(this.isMovingTimeout);
            this.isMovingTimeout = undefined;
        }

        window.removeEventListener('wheel', this.scrollScreen);
        window.removeEventListener('resize', this._updateAfterProcessHasEnded);
        this.removeRegisteredListeners(this.elementListeners);
        this.removeRegisteredListeners(this.trackContainerListeners);

        if (this._onClientDraw) {
            this.chatApi.removeListener('clientDrawings', this._onClientDraw);
        }
        if (this._onDashboardDraw) {
            this.chatApi.removeListener('dashboardDrawings', this._onDashboardDraw);
        }
        this.localDrawingCanvas?.removeEventListener('drawingCanvasStartDraw', this.onDrawingCanvasStartDraw);
        this.localDrawingCanvas?.removeEventListener('drawingCanvasDraw', this.onDrawingCanvasDraw);
        this.localDrawingCanvas?.removeEventListener('drawingCanvasEndDraw', this.onDrawingCanvasEndDraw);

        if (this.canvasDrawer) {
            this.canvasDrawer.destroy();
        }

        this.canvasDrawer = null;

        if (this.remoteCanvasDrawer) {
            this.remoteCanvasDrawer.destroy();
        }

        this.remoteCanvasDrawer = null;

        this._annotationCanvas = null;
        this._localDrawingCanvas = null;
        this._remoteDrawingCanvas = null;

        this.destroyVideoSubscriber();

        this.hideElement(this._pointer);
        this.hideElement(this._element);

        this._disposeMainContainerSizeSensor();

        this._disposers.forEach((dispose) => dispose());
    }

    _setVideoStreamPlayingFalse() {
        runInAction(() => {
            this.videoState.isVideoStreamPlaying = false;
        });
    }

    _updateCanvas(_, data) {
        if (!data.length) {
            return;
        }

        const remoteCanvas =
            this.remoteDrawingCanvas instanceof HTMLElement ? this.remoteDrawingCanvas : this.remoteDrawingCanvas?.[0];

        if (!remoteCanvas) {
            return;
        }

        const dashboardSettings = this.videoState.magicMarker?.dashboard;
        const clientSettings = this.videoState.magicMarker?.client;
        const colorSettings = this.currentDrawingSource === UserType.dashboard ? dashboardSettings : clientSettings;

        if (this.remoteCanvasDrawer) {
            this.remoteCanvasDrawer.destroy();
        }

        const drawingOptions = {
            lineWidth: colorSettings.lineWidth,
            lineWidthRefResolution: {width: 640, height: 480},
            strokeColorRGB: colorSettings.strokeColorRgb
        };

        this.remoteCanvasDrawer = new CanvasDrawer(
            remoteCanvas,
            drawingOptions,
            undefined,
            colorSettings.timeUntilDrawingStartFading
        );

        this.remoteCanvasDrawer.drawPoints(data, {
            width: remoteCanvas.width,
            height: remoteCanvas.height
        });
    }

    applySettings() {
        trace.info('applySettings');
        // If it the thumbnail is being moved, we prevent it from becoming
        // full when the mouse button is released
        if (this._isMoving) {
            this._isMoving = false;
            runInAction(() => {
                this.videoState.focus = false;
            });
            this.minimizedVideoMoved = true;
        } else if (this.videoState.focus) {
            this.minimizedVideoMoved = false;
        }

        this.toggleClass(this._element, 'video-focused', !!this.videoState.focus);
        this.toggleClass(this._element, 'video-minimized', !this.videoState.focus);
        this.toggleClass(this._element, 'video-cursor-sharing', this.videoState.isCursorSharingActive);
        this.toggleClass(this._element, 'video-scanning', this.videoState.mode === MODES.SCANNING);

        const annotationCanvas = this.annotationCanvas;
        if (annotationCanvas) {
            annotationCanvas.style.zIndex = this.videoState.mode === MODES.CURSOR_SHARING ? '-1' : '';
        }

        const sizeTarget = this.normalizeElement(document.querySelector(this.videoState.sizeTarget));

        if (!this.videoState.enabled) {
            const padding = this.tsInterfaceHelper.getPadding;

            this._containerWidth = Math.max(
                this.getElementWidth(sizeTarget) - 100,
                this.getElementWidth(this._element) - 100,
                window.innerWidth - padding.width
            );

            this._containerHeight = Math.max(
                this.getElementHeight(sizeTarget),
                this.getElementHeight(this._element),
                window.innerHeight - padding.height
            );

            this.hideElement(this._element);
        }

        if (this.videoState.enabled) {
            if (!this.videoSubscriber && this._element) {
                const requestedElement = this._element;
                const subscriberParams = {
                    streamType: KnownMediaStream.USER_VIDEO_STREAM,
                    container: requestedElement
                };

                this.mediaService.createSubscriber(subscriberParams).then((subscriber) => {
                    // If _element changed (unmount/remount race) while createSubscriber was in flight,
                    // discard the result instead of storing a subscriber the controller no longer wants.
                    if (this._element !== requestedElement) {
                        this.mediaService.destroySubscriber(subscriber.container);
                        return;
                    }
                    this.videoSubscriber = subscriber;

                    subscriber.onStateChanged(this._videoSubscriberStateChanged);
                    subscriber.onDispose(() => {
                        this.videoSubscriber = null;
                        this._setVideoStreamPlayingFalse();
                    });
                    subscriber.onRenderError((error) => {
                        this._eventLog.videoRenderError({
                            message: error?.message ?? String(error),
                            name: error?.name,
                            code: error?.code
                        });
                    });
                    this.onVideoSubscriberChanged && this.onVideoSubscriberChanged(subscriber);

                    if (this.videoState.enableDashboardMagicMarker) {
                        const localCanvas = this.localDrawingCanvas;
                        if (localCanvas) {
                            this.initLocalDrawingCanvas(localCanvas);
                        }
                    }
                });
            }
        }

        if (this.videoState.mode === MODES.SCANNING && this.dashboardState.isToggleEnabled_ScanCaptureDesign) {
            setTimeout(() => this._initScanMode(), 300);
        }

        if (
            this.videoState.mode !== MODES.SCANNING &&
            this.tsScanArea.initialized &&
            this.dashboardState.isToggleEnabled_ScanCaptureDesign
        ) {
            this.tsScanArea.destroy();
            this._annotationCanvas = null;
        }

        this._updateAfterProcessHasEnded();
    }

    _videoSubscriberStateChanged(videoResolution) {
        if (!this.videoSubscriber) {
            return;
        }

        if (videoResolution) {
            this.videoResolutionValue = videoResolution.resolutionFormat;
            this._videoResolutionDimensionChange(videoResolution.resolutionFormat);
        }

        this.tsSnapshotReminder.videoReady(this.videoSubscriber.isPlaying);

        runInAction(() => {
            this.videoState.isVideoStreamPlaying = !!this.videoSubscriber?.isPlaying;
        });
        if (this.videoSubscriber.isPlaying) {
            trace.info('Video is playing');
            this._bindTrackContainer();

            this.showElement(this._element);
            this.applySettings();
        } else {
            trace.info('Video is NOT playing');
            this.removeRegisteredListeners(this.trackContainerListeners);

            this.hideElement(this._pointer);
        }
        this._updateFrameAndOffset();
    }

    private _bindTrackContainer() {
        const trackContainer =
            !this.mobile && !this.isMiniDashboard
                ? this._element?.querySelector('.video-tools-container')
                : this._getVideoRootElement();

        if (!trackContainer || this.trackContainerListeners[0]?.target === trackContainer) {
            return;
        }

        const [start, move, cancel] = this.mobile
            ? ['touchstart', 'touchmove', 'touchcancel']
            : ['mousedown', 'mousemove', 'mouseleave'];

        this.removeRegisteredListeners(this.trackContainerListeners);
        this.addTrackContainerListener(trackContainer, start, (e) => this._detectCursor(e));
        this.addTrackContainerListener(trackContainer, move, (e) => this._detectCursor(e));
        this.addTrackContainerListener(trackContainer, cancel, () => this._detectCursor(null));

        if (!this.mobile) {
            this.addTrackContainerListener(trackContainer, 'auxclick', (e) => this._mouseClick(e));
            this.addTrackContainerListener(trackContainer, 'contextmenu', (e) => e.preventDefault());
        }
    }

    _initScanMode() {
        const canvas = this.annotationCanvas;

        if (canvas) {
            this.tsScanArea.init(canvas);
        }
    }

    _updateOffset(rootElement) {
        const root = rootElement ? rootElement : this._getVideoRootElement();

        if (root) {
            // If it's a jQuery-like object with length property
            if (typeof root === 'object' && 'length' in root) {
                if (root.length > 0) {
                    const element = root[0] instanceof HTMLElement ? root[0] : root;
                    if (element instanceof HTMLElement) {
                        const offset = this.getOffset(element);
                        this._frame.top = offset.top;
                        this._frame.left = offset.left;
                    }
                }
            }
            // If it's a plain HTMLElement
            else if (root instanceof HTMLElement) {
                const offset = this.getOffset(root);
                this._frame.top = offset.top;
                this._frame.left = offset.left;
            }
        }
    }

    _updateFrame() {
        const root = this._getVideoRootElement();

        if (!root || !this.videoSubscriber || !this.videoSubscriber.isPlaying) {
            return;
        }

        if (!this.mobile && this.videoStreamWidth > 0 && this.videoStreamHeight > 0) {
            const minimizedProcessFlag = this.getDatasetValue(this._element, 'isMinimizedProcessed');

            if (!this.videoState.focus && !minimizedProcessFlag) {
                this.setStyles(this._element, {display: 'none'});
                this.setDatasetValue(this._element, 'isMinimizedProcessed', 'true');
            } else if (this.videoState.focus) {
                this.setDatasetValue(this._element, 'isMinimizedProcessed', undefined);
            }

            this.updateFrameNew(root);
            return;
        }

        // Get offset from HTMLElement
        const offset = this.getOffset(root);
        this._frame.top = offset.top;
        this._frame.left = offset.left;

        this._getFrameDimensions();

        if (this.videoState.focus) {
            this.minimizedVideoMoved = false;
            this.setStyles(this._element, {top: '', left: '', position: 'relative'});

            // Set dimensions on root video element
            root.style.width = `${this._frame.width}px`;
            root.style.height = `${this._frame.height}px`;
        } else {
            this._detectCursor(null);
            this.hideElement(this._pointer);

            if (this.minimizedVideoMoved) {
                return;
            }

            const minimizedVideoDimensions = this._getMinimizedVideoDimensions();

            // Set dimensions on root video element
            root.style.width = `${minimizedVideoDimensions.width}px`;
            root.style.height = `${minimizedVideoDimensions.height}px`;

            this.setDimensions(this._element, minimizedVideoDimensions.width, minimizedVideoDimensions.height);

            if (!this.mobile) {
                const offset = this._getMinimizeVideoOffset(minimizedVideoDimensions);
                this.setStyles(this._element, {
                    top: `${offset.top}px`,
                    left: `${offset.left}px`,
                    position: 'absolute'
                });
            }
        }

        // Handle layout issues in IE
        if (this.isIE11) {
            this.setDimensions(this._element, root.clientWidth, root.clientHeight);

            const videoElement = this._element?.querySelector('.OT_video-element');
            if (videoElement && videoElement instanceof HTMLElement) {
                videoElement.style.width = '';
                videoElement.style.height = '';
            }
        }

        this._updateOffset(root);

        const isLandscapeViewOnMobile = this.mobile && this.responsiveUtils.isLandscape();

        if (isLandscapeViewOnMobile) {
            this.setStyles(this._element, {
                width: `${this._frame.width}px`,
                height: `${this._frame.height}px`
            });
        }

        this._updateCanvasesDimensions();

        if (!this.isIE11) {
            const canvas = this.annotationCanvas;
            if (canvas) {
                canvas.style.marginLeft = `${-this._frame.width / 2}px`;
            }
        }

        const requestApprovalEl = this._element?.querySelector('.request-approval');
        if (requestApprovalEl && requestApprovalEl instanceof HTMLElement) {
            requestApprovalEl.style.marginLeft = `${this._frame.width + 10}px`;
        }
    }

    updateFrameNew(videoElement) {
        const videoMeta = new VideoMetaData(this.videoStreamWidth, this.videoStreamHeight);

        let videoStrategy = null;

        if (!this.videoState.focus) {
            setTimeout(() => {
                this.isFullScreenVideo = false;
            }, 0);

            switch (this.dashboardState.minimizedVideoMode) {
                case VIDEO_MINIMIZED_MODES.CIRCLE:
                    videoStrategy = new MinimizedCircleVideoStrategy(this.window);

                    break;

                case VIDEO_MINIMIZED_MODES.SQUARE:
                    videoStrategy = new MinimizedSquareVideoStrategy(this.window);
                    break;

                default:
                    videoStrategy = new OldMinimizedVideoStrategy(this.window, (newVideoSize) =>
                        this._getMinimizeVideoOffset(newVideoSize)
                    );
                    break;
            }

            if (
                this.dashboardState.minimizedVideoMode === VIDEO_MINIMIZED_MODES.CIRCLE ||
                this.dashboardState.minimizedVideoMode === VIDEO_MINIMIZED_MODES.SQUARE
            ) {
                this.setStyles(this._element, {width: '', height: ''});
            }
        } else {
            setTimeout(() => {
                this.isFullScreenVideo = true;
            }, 0);
            videoStrategy = new StretchedVideoStrategy(this.annotationCanvas, this.window);
        }

        const videoNewSize = videoStrategy.getVideoNewSize(videoMeta);

        videoStrategy.updateVideoSize(videoElement, videoNewSize, videoMeta);
        videoStrategy.updateVideoLocation(
            videoNewSize,
            this._element,
            !this.videoState.focus ? this.dashboardState.minimizedVideoMoved : false
        );

        this._frame.width = videoNewSize.width;
        this._frame.height = videoNewSize.height;

        // Get offset from vanilla element
        const element = videoElement instanceof HTMLElement ? videoElement : videoElement[0];
        if (element) {
            const offset = this.getOffset(element);
            this._frame.top = offset.top;
            this._frame.left = offset.left;
        }

        this._updateCanvasesDimensions();
    }

    _updateCanvasesDimensions() {
        if (this.videoState.enableDashboardMagicMarker && this.localDrawingCanvas) {
            // Get the canvas element (could be HTMLElement or jQuery-like object)
            const localCanvas =
                this.localDrawingCanvas instanceof HTMLElement ? this.localDrawingCanvas : this.localDrawingCanvas[0];

            if (localCanvas) {
                localCanvas.width = this.videoStreamWidth;
                localCanvas.height = this.videoStreamHeight;
                localCanvas.style.width = `${this._frame.width}px`;
                localCanvas.style.height = `${this._frame.height}px`;
            }
        }

        if (this.videoState.enableCustomerMagicMarker && this.remoteDrawingCanvas) {
            // Get the canvas element (could be HTMLElement or jQuery-like object)
            const remoteCanvas =
                this.remoteDrawingCanvas instanceof HTMLElement
                    ? this.remoteDrawingCanvas
                    : this.remoteDrawingCanvas[0];

            if (remoteCanvas) {
                remoteCanvas.width = this.videoStreamWidth;
                remoteCanvas.height = this.videoStreamHeight;
                remoteCanvas.style.width = `${this._frame.width}px`;
                remoteCanvas.style.height = `${this._frame.height}px`;
            }
        }
    }

    _getMinimizeVideoOffset(videoDimensions) {
        if (!this.tsInterfaceHelper.useNewInterface) {
            return {
                top: mediaFrameUIParams.MINIMIZED_VIDEO_TOP_OFFSET,
                left: mediaFrameUIParams.MINIMIZED_VIDEO_LEFT_OFFSET
            };
        }

        const minimizedOffset = {};

        if (this.isMinimizedToBottomRight) {
            const verticalFactor =
                mediaFrameUIParams.MINIMIZED_VIDEO_RIGHT_OFFSET_NEW_BOTTOM_RIGHT + videoDimensions.width + 10;
            const horizontalFactor =
                mediaFrameUIParams.MINIMIZED_VIDEO_TOP_OFFSET_NEW_BOTTOM_RIGHT + videoDimensions.height + 10;

            minimizedOffset.top = this.window.innerHeight - horizontalFactor;
            minimizedOffset.left = this.window.innerWidth - verticalFactor;
        } else {
            const verticalFactor = (mediaFrameUIParams.MINIMIZED_VIDEO_MAX_WIDTH - videoDimensions.width) / 2;

            minimizedOffset.top = mediaFrameUIParams.MINIMIZED_VIDEO_TOP_OFFSET_NEW;
            minimizedOffset.left = mediaFrameUIParams.MINIMIZED_VIDEO_LEFT_OFFSET_NEW + verticalFactor;
        }

        return minimizedOffset;
    }

    _getFrameDimensions() {
        const padding = this.tsInterfaceHelper.getPadding;

        this._containerWidth =
            this.window.innerWidth - padding.width - mediaFrameUIParams.MOBILE_VIDEO_CONTAINER_PADDING;
        this._containerHeight = this.window.innerHeight - padding.height;

        if (this._containerWidth / this._containerHeight > this.videoStreamWidth / this.videoStreamHeight) {
            this._frame.height = this._containerHeight;
            this._frame.width = (this._containerHeight * this.videoStreamWidth) / this.videoStreamHeight;
        } else {
            this._frame.width = this._containerWidth;
            this._frame.height = (this._containerWidth * this.videoStreamHeight) / this.videoStreamWidth;
        }
    }

    _getMinimizedVideoDimensions() {
        if (this.mobile) {
            return {
                width: mediaFrameUIParams.MOBILE_DASHBOARD_MINIMIZED_VIDEO_WIDTH,
                height: mediaFrameUIParams.MOBILE_DASHBOARD_MINIMIZED_VIDEO_HEIGHT
            };
        }

        const minimizedVideoDimensions = {};

        if (this.videoStreamWidth > this.videoStreamHeight) {
            minimizedVideoDimensions.width = mediaFrameUIParams.MINIMIZED_VIDEO_MAX_WIDTH;
            minimizedVideoDimensions.height =
                (mediaFrameUIParams.MINIMIZED_VIDEO_MAX_HEIGHT * this.videoStreamHeight) / this.videoStreamWidth;
        } else {
            minimizedVideoDimensions.width =
                (mediaFrameUIParams.MINIMIZED_VIDEO_MAX_WIDTH * this.videoStreamWidth) / this.videoStreamHeight;
            minimizedVideoDimensions.height = mediaFrameUIParams.MINIMIZED_VIDEO_MAX_HEIGHT;
        }

        return minimizedVideoDimensions;
    }

    _getCursorPos(e) {
        const ev = e.originalEvent;

        const pageX = get(e, 'touches[0].pageX') || get(ev, 'changedTouches[0].pageX') || e.pageX;
        const pageY = get(e, 'touches[0].pageY') || get(ev, 'changedTouches[0].pageY') || e.pageY;

        const x = (this.videoStreamWidth * (pageX - this._frame.left)) / this._frame.width,
            y = (this.videoStreamHeight * (pageY - this._frame.top)) / this._frame.height;

        return {
            x: Math.max(0, Math.min(x, this.videoStreamWidth)),
            y: Math.max(0, Math.min(y, this.videoStreamHeight)),
            originalX: pageX,
            originalY: pageY
        };
    }

    _hasWorkingVideoForPointer(): boolean {
        return !!(
            this.videoSubscriber?.isPlaying &&
            this.videoStreamWidth > 0 &&
            this.videoStreamHeight > 0 &&
            this.videoState.focus &&
            this._frame.width > 0 &&
            this._frame.height > 0
        );
    }

    _displayLivePointerForAgent(cursorPosition) {
        if (!this._hasWorkingVideoForPointer()) {
            if (this._pointer) this.hideElement(this._pointer);
            return;
        }

        const maxPointerPositionX = Math.min(cursorPosition.originalX, this._frame.width + this._frame.left);

        const updatedCursor = {
            x: Math.max(this._frame.left, maxPointerPositionX) - this._frame.left,
            y: Math.max(0, Math.min(cursorPosition.originalY, this._frame.top + this._frame.height) - this._frame.top)
        };

        if (this._pointer) {
            this._pointer.style.left = Math.round(updatedCursor.x) + 'px';
            this._pointer.style.top = Math.round(updatedCursor.y) + 'px';
            this._pointer.style.display = '';
        }
    }

    _mouseClick(e) {
        if (this.dashboardState.videoModeState.videoState.remoteControl) {
            return;
        }

        switch (e.button) {
            case 0:
                this.mouseButtonPosition = MouseButtonPosition.Left;
                break;
            case 1:
                this.mouseButtonPosition = MouseButtonPosition.Middle;
                break;
            case 2:
                this.mouseButtonPosition = MouseButtonPosition.Right;
                break;
            default:
                this.mouseButtonPosition = MouseButtonPosition.Left;
        }
    }

    _detectCursor(e) {
        if (this.videoState.mode !== MODES.CURSOR_SHARING || (!this.isLivePointerEnabled && this.mobile)) {
            return;
        }

        if (e && e.target) {
            const target = e.target as Element | null;

            if ((!target || !target.classList.contains('video-tools-container')) && !this.isMiniDashboard) {
                this.chatApi.setStatus('cursor', null, true);

                return;
            }
        }

        if (
            e &&
            this.videoState.focus &&
            this.videoState.enabled &&
            !this.videoState.isPaused &&
            !!this.videoSubscriber?.isPlaying
        ) {
            this.updateCursorPosition(e);

            return;
        }

        this.chatApi.setStatus('cursor', null, true);
    }

    _initMainContainerSizeSensor() {
        if (!this._mainContainerSizeSensor) {
            const mainContainerElement = document.querySelector('.dashboard-main-content-container');

            if (!mainContainerElement) {
                return;
            }

            this._mainContainerSizeSensor = new ResizeSensor(mainContainerElement, () => {
                this._updateFrameAndOffset();
            });
        }
    }

    _updateCursorPosition(e, extraCursorData) {
        if (!this.videoSubscriber?.isPlaying) {
            this.chatApi.setStatus('cursor', null, true);
            return;
        }
        const cursorPosition = this._getCursorPos(e);
        const cursorData = assign({...extraCursorData}, this._getCursorPos(e), {
            image: {
                width: this.videoStreamWidth,
                height: this.videoStreamHeight
            },
            mousePressStatus: this.mousePressStatus,
            buttonPosition: this.mouseButtonPosition
        });

        if (
            this.mobile &&
            (!this.dashboardState.videoModeState.videoState.isLivePointerOnMobileDashboardEnabled ||
                this.dashboardState.videoModeState.videoState.enableDashboardMagicMarker)
        ) {
            return;
        }

        if (this._pointer) {
            this._displayLivePointerForAgent(cursorPosition);
        }

        this.chatApi.setStatusFast('cursor', cursorData);
    }

    _disposeMainContainerSizeSensor() {
        if (this._mainContainerSizeSensor && this._mainContainerSizeSensor.reset) {
            ResizeSensor.detach(document.querySelector('.dashboard-main-content-container'));
            this._mainContainerSizeSensor = null;
        }
    }

    _updateAfterProcessHasEnded() {
        this.timeOutId = setTimeout(() => {
            if (this.videoSubscriber?.isPlaying) {
                this._bindTrackContainer();
            }
            this._updateFrameAndOffset();
        }, 0);
    }

    _getVideoRootElement(): HTMLElement | null {
        return this.videoSubscriber ? this.videoSubscriber.mediaElement : null;
    }

    _updateFrameAndOffset() {
        this._updateOffset();
        this.updateFrame();
    }

    _scrollScreenOnce(event) {
        const scrollData = {x: 0, y: (event.deltaY + event.wheelDeltaY) / 2};

        this.chatApi.setStatusFast('mouseScroll', scrollData, true);
    }

    _videoResolutionDimensionChange(videoResolution) {
        return this.videoState.focus
            ? this.onVideoResolutionChanged({resolutionFormat: videoResolution})
            : this.onVideoResolutionChanged({resolutionFormat: VideoResolutionFormat.NONE});
    }

    _handleClickType() {
        if (this.mouseClicks === null) {
            this.mouseClicks = 0;
        }

        this.mouseClicks++;

        if (this.mouseClicks === 1) {
            this.mouseClickType = mouseClickType.Click;
            this.timer = setTimeout(() => {
                this.mouseClicks = 0;
            }, 200);
        } else {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = undefined;
            }
            this.mouseClickType = mouseClickType.DblClick;
            this.mouseClicks = 0;
        }
    }

    private get isSnapshotEligible(): boolean {
        return this.isFullScreenVideo && this.canTakeScreenshot;
    }

    canTriggerClick(): boolean {
        if (this._isMoving) return false;
        if (!this._hadVideoFocusAtPointerStart) return false;
        return this.isSnapshotEligible;
    }

    sendEvent(eventName: VideoSubscriberEventType, data: any) {
        this.emit(eventName, data);
    }

    private destroyVideoSubscriber(): void {
        if (this.videoSubscriber) {
            this.mediaService.destroySubscriber(this.videoSubscriber.container);
            this.videoSubscriber = null;
            this._setVideoStreamPlayingFalse();
            this.onVideoSubscriberChanged?.(null);
        }
    }

    cleanupSubscriber() {
        this.removeRegisteredListeners(this.elementListeners);
        this.removeRegisteredListeners(this.trackContainerListeners);

        if (this._onClientDraw) {
            this.chatApi.removeListener('clientDrawings', this._onClientDraw);
            this._onClientDraw = null;
        }
        if (this._onDashboardDraw) {
            this.chatApi.removeListener('dashboardDrawings', this._onDashboardDraw);
            this._onDashboardDraw = null;
        }
        if (this._onDraw) {
            this._onDraw = null;
        }
        window.removeEventListener('resize', this._updateAfterProcessHasEnded);

        // 4. Destroy canvas drawers
        if (this.canvasDrawer) {
            this.canvasDrawer.destroy();
            this.canvasDrawer = null;
        }

        if (this.remoteCanvasDrawer) {
            this.remoteCanvasDrawer.destroy();
            this.remoteCanvasDrawer = null;
        }

        // 5. Destroy video subscriber
        this.destroyVideoSubscriber();

        // 6. Reset canvas references
        this._annotationCanvas = null;
        this._localDrawingCanvas = null;
        this._remoteDrawingCanvas = null;

        // 7. Reset element reference
        this._element = null;
    }
}
